"use client";

/**
 * NewForm design-system Modal.
 *
 * Wraps Radix Dialog with DS tokens — fonts (Rethink Sans), spacing
 * (`*-nf-ds-*`), radii (`rounded-nf-ds-overlay`), shadows (`shadow-nf-ds-xl`),
 * and the white card pattern used by the rest of the design system. The
 * compound surface mirrors the legacy `@/components/ui/dialog` so call
 * sites can swap imports incrementally:
 *
 *   import {
 *     Modal,
 *     ModalContent,
 *     ModalHeader,
 *     ModalTitle,
 *     ModalDescription,
 *     ModalFooter,
 *   } from "@/components/ui/design-system/modal";
 *
 *   <Modal open={open} onOpenChange={setOpen}>
 *     <ModalContent size="lg">
 *       <ModalHeader>
 *         <ModalTitle>Delete batch</ModalTitle>
 *         <ModalDescription>
 *           This permanently removes the batch and its monitoring data.
 *         </ModalDescription>
 *       </ModalHeader>
 *       <p>…body…</p>
 *       <ModalFooter>
 *         <Button variant="ghost">Cancel</Button>
 *         <Button variant="brand">Delete</Button>
 *       </ModalFooter>
 *     </ModalContent>
 *   </Modal>
 *
 * Sizes (`max-w`): sm (md), default (lg), lg (2xl), xl (4xl), 2xl (5xl).
 * Pass `hideClose` on `ModalContent` to drop the corner X — useful for
 * confirmations that demand an explicit footer choice.
 */

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { CONTROL_PRESS } from "./surface-styles";

const Modal = DialogPrimitive.Root;
const ModalTrigger = DialogPrimitive.Trigger;
const ModalPortal = DialogPrimitive.Portal;
const ModalClose = DialogPrimitive.Close;

const ModalOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // A SCRIM IS BLACK, not the foreground colour. `bg-foreground/40` only
      // reads as a scrim in light, where the foreground happens to be
      // near-black; in dark the foreground is near-*white*, so this washed the
      // page lighter behind the dialog instead of pushing it back — the exact
      // opposite of what a scrim is for. The light value is left at the
      // foreground it has always resolved to, so only dark changes; it goes
      // heavier than 40% because black over an already-dark ground has less
      // room to work.
      "fixed inset-0 z-50 bg-foreground/40 dark:bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
ModalOverlay.displayName = "ModalOverlay";

/**
 * How long `ModalContent`'s open animation runs, for callers that need to wait
 * it out — deferring expensive work in the body until the card has landed, say.
 * Kept in step with the `duration-[250ms]` below by hand: Tailwind needs the
 * class as a literal, so it cannot read this.
 */
export const MODAL_ENTER_MS = 250;

const modalContentVariants = cva(
  // Pure scale-in/out (no slide). The card grows from 92% → 100% on
  // open and shrinks 100% → 92% on close, paired with a fade. Origin
  // stays at the visual center thanks to the 50% translate offsets,
  // so the scale reads as an anchored pop rather than a corner zoom.
  // `ease-[cubic-bezier(0.32,0.72,0,1)]` is the Apple-style curve we
  // use elsewhere in the DS for snappy modal-class transitions.
  // The height cap + `overflow-y-auto` are the default because a centered
  // fixed card has no other way out: content taller than the viewport spills
  // past both edges and Radix has already locked body scroll, so the overflow
  // is unreachable. Call sites that manage their own scrolling (a sticky
  // header over a scrolling body, `p-0` media surfaces) override both through
  // `cn`'s tailwind-merge.
  "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] w-full max-h-[calc(100dvh-2rem)] overflow-y-auto bg-popover border border-border shadow-nf-ds-xl rounded-nf-ds-overlay p-nf-ds-6 font-nf-ds-sans origin-center duration-[250ms] ease-[cubic-bezier(0.32,0.72,0,1)] outline-hidden focus-visible:outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
  {
    variants: {
      size: {
        sm: "max-w-md",
        default: "max-w-lg",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
        "2xl": "max-w-5xl",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

interface ModalContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof modalContentVariants> {
  hideClose?: boolean;
}

const ModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ModalContentProps
>(({ className, size, hideClose = false, children, ...props }, ref) => (
  <ModalPortal>
    <ModalOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(modalContentVariants({ size }), className)}
      {...props}
    >
      {children}
      {hideClose ? null : (
        <DialogPrimitive.Close
          className={cn(
            "absolute top-nf-ds-3 right-nf-ds-3 flex h-7 w-7 items-center justify-center rounded-nf-ds-sm text-muted-foreground hover:text-foreground hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            CONTROL_PRESS,
          )}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </ModalPortal>
));
ModalContent.displayName = "ModalContent";

// Header pads its right edge so the title can't run under the corner
// close button. Modal title scale sits between DS card titles (16/600)
// and the page header (28/600) — chosen for prominence appropriate to
// a modal-class surface.
const ModalHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col gap-nf-ds-2 text-left mb-nf-ds-5 pr-nf-ds-6",
      className,
    )}
    {...props}
  />
);
ModalHeader.displayName = "ModalHeader";

const ModalFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-nf-ds-2 mt-nf-ds-6 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);
ModalFooter.displayName = "ModalFooter";

const ModalTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "font-nf-ds-sans text-[20px] font-[600] leading-[1.2] tracking-[-0.01em] text-foreground",
      className,
    )}
    {...props}
  />
));
ModalTitle.displayName = "ModalTitle";

const ModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      "font-nf-ds-sans text-[14px] text-muted-foreground leading-[1.55]",
      className,
    )}
    {...props}
  />
));
ModalDescription.displayName = "ModalDescription";

export {
  Modal,
  ModalTrigger,
  ModalPortal,
  ModalClose,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
};
